// =============================================
// Micro-EAV Generator — generate EAV for KG entities that lack it
// Runs after KG Assembly, before Fan-Out in Etap 1
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { parseLLMJSON, getLLMProvider, getStageConfig } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import { logPublic, logDebug } from "./contentLogger";

const MAX_ENTITIES_PER_CALL = 10;

interface KGEntity {
  entity_name: string;
  description?: string;
  entity_type?: string; // np. "Person" | "Organization" | "Brand" | "Product" | "Concept"
}

interface MapContext {
  map_name: string;
  macro_statement: string;
  scope_in: string[];
  scope_out: string[];
}

/**
 * Generate EAV attributes for KG entities that don't have them in DB.
 * Creates thematic_entity records if needed, then generates EAV via LLM.
 */
export interface MicroEavDebug {
  entities_processed: number;
  llm_response_chars: number;
  raw_response_sample: string; // first 1000 chars of LLM response
  generated_count: number;
  skipped_empty_attrs: number; // LLM returned attributes: []
  lookup_misses: number; // LLM canonical_name nie matchuje existingNameToId (silent-drop fix)
  parse_error: string | null;
  model_used: string;
  provider_used: string;
  error?: string; // prompt_not_found etc
}

function emptyDebug(overrides: Partial<MicroEavDebug> = {}): MicroEavDebug {
  return {
    entities_processed: 0,
    llm_response_chars: 0,
    raw_response_sample: "",
    generated_count: 0,
    skipped_empty_attrs: 0,
    lookup_misses: 0,
    parse_error: null,
    model_used: "",
    provider_used: "",
    ...overrides,
  };
}

// DB check constraint: value_type IN ('text', 'number', 'boolean', 'enum').
// LLM czasem zwraca synonimy ("string", "date", "list") — normalizujemy na DB-enum.
// Bez tego INSERT padal z "violates check constraint thematic_entity_attributes_value_type_check"
// i 10/10 EAV odrzuconych po udanym LLM response (run 2026-04-22T03:57).
const DB_ALLOWED_VALUE_TYPES = new Set(["text", "number", "boolean", "enum"]);

function normalizeValueType(raw: string | undefined | null): string {
  if (!raw) return "text";
  const v = raw.toLowerCase().trim();
  if (DB_ALLOWED_VALUE_TYPES.has(v)) return v;
  // Common LLM aliases → DB-compatible mapping
  if (v === "string" || v === "date" || v === "datetime" || v === "timestamp") return "text";
  if (v === "list" || v === "array" || v === "categorical") return "enum";
  if (v === "integer" || v === "float" || v === "decimal" || v === "numeric") return "number";
  if (v === "bool") return "boolean";
  return "text"; // safe fallback — lepiej text niz crash na unknown
}

// DB check constraint: entity_type IN ('concept', 'product', 'method', 'tool', 'role', 'problem', 'attribute').
// KG Assembler zwraca m.in. ORGANIZATION / LOCATION / PERSON / BRAND / EVENT — bez mapowania
// INSERT padal silently i 6/10 encji nie trafialo do thematic_entities (run 2026-04-22T05:37).
const DB_ALLOWED_ENTITY_TYPES = new Set([
  "concept", "product", "method", "tool", "role", "problem", "attribute",
]);

function normalizeEntityType(raw: string | undefined | null): string {
  if (!raw) return "concept";
  const v = raw.toLowerCase().trim();
  if (DB_ALLOWED_ENTITY_TYPES.has(v)) return v;
  if (v === "person" || v === "people") return "role";
  // wszystko inne (organization, brand, location, event, work, language, ...) → concept
  return "concept";
}

export async function generateMicroEav(
  nodeId: string,
  mapId: string,
  kgEntities: KGEntity[],
  language: string,
  mapContext: MapContext,
  nodeTitle: string,
  documentRole: string,
  canonicalQuestion: string,
): Promise<{ generatedCount: number; costUsd: number; entityNameToDbId: Record<string, string>; debug: MicroEavDebug }> {
  if (!kgEntities || kgEntities.length === 0) {
    return { generatedCount: 0, costUsd: 0, entityNameToDbId: {}, debug: emptyDebug() };
  }

  // 1. Get existing entities for this map (canonical_name + map_id match)
  const { data: existingEntities } = await (supabase as any)
    .from("thematic_entities")
    .select("id, canonical_name")
    .eq("map_id", mapId);

  const existingNameToId = new Map<string, string>();
  for (const e of existingEntities || []) {
    existingNameToId.set(e.canonical_name.toLowerCase(), e.id);
  }

  // 2. Check which existing entities already have EAV
  const existingIds = [...existingNameToId.values()];
  const entitiesWithEav = new Set<string>();
  if (existingIds.length > 0) {
    const { data: eavCounts } = await (supabase as any)
      .from("thematic_entity_attributes")
      .select("entity_id")
      .in("entity_id", existingIds);
    for (const row of eavCounts || []) {
      entitiesWithEav.add(row.entity_id);
    }
  }

  // 3. Filter KG entities: only those WITHOUT EAV (max 10)
  const entitiesToProcess: KGEntity[] = [];
  for (const kgE of kgEntities) {
    if (entitiesToProcess.length >= MAX_ENTITIES_PER_CALL) break;
    const name = kgE.entity_name.toLowerCase();
    const existingId = existingNameToId.get(name);
    if (existingId && entitiesWithEav.has(existingId)) {
      continue; // Already has EAV → skip
    }
    entitiesToProcess.push(kgE);
  }

  if (entitiesToProcess.length === 0) {
    logDebug("MICRO-EAV", "All KG entities already have EAV — skip");
    return {
      generatedCount: 0,
      costUsd: 0,
      entityNameToDbId: Object.fromEntries(existingNameToId),
      debug: emptyDebug(),
    };
  }

  logPublic("MICRO-EAV", `${entitiesToProcess.length} KG entities without EAV: ${entitiesToProcess.map(e => e.entity_name).join(", ")}`);

  // 4. Create thematic_entity records for entities that don't exist yet
  const { data: session } = await supabase.auth.getSession();
  const userId = session?.session?.user?.id;
  if (!userId) {
    logDebug("MICRO-EAV", "No auth session — skip");
    return { generatedCount: 0, costUsd: 0, entityNameToDbId: {}, debug: emptyDebug({ error: "no_auth_session" }) };
  }

  let entityCreateFailures = 0;
  for (const kgE of entitiesToProcess) {
    const name = kgE.entity_name.toLowerCase();
    if (!existingNameToId.has(name)) {
      const normalizedType = normalizeEntityType(kgE.entity_type);
      const { data: inserted, error: insertError } = await (supabase as any)
        .from("thematic_entities")
        .insert({
          user_id: userId,
          map_id: mapId,
          canonical_name: kgE.entity_name,
          entity_type: normalizedType,
          aliases: [],
          description: kgE.description || "",
        })
        .select("id")
        .single();
      if (inserted) {
        existingNameToId.set(name, inserted.id);
        logDebug("MICRO-EAV", `Created entity: "${kgE.entity_name}" [${normalizedType}] → ${inserted.id}`);
      } else {
        entityCreateFailures++;
        logDebug(
          "MICRO-EAV",
          `Entity INSERT failed: "${kgE.entity_name}" [raw_type="${kgE.entity_type || ""}" → "${normalizedType}"] — ${insertError?.message || "unknown"}`,
        );
      }
    }
  }
  if (entityCreateFailures > 0) {
    logPublic("MICRO-EAV", `WARN: ${entityCreateFailures} entity INSERT failures (silent-drop before fix)`);
  }

  // 5. Load prompt — fail-fast jesli brak w DB (bez sushi fallback)
  const dbPrompt = await loadPrompt("eav_extraction");
  if (!dbPrompt?.template) {
    logPublic("MICRO-EAV", "FATAL: eav_extraction prompt not found in DB — aborting");
    return {
      generatedCount: 0,
      costUsd: 0,
      entityNameToDbId: Object.fromEntries(existingNameToId),
      debug: emptyDebug({ error: "prompt_not_found", entities_processed: entitiesToProcess.length }),
    };
  }
  if (!dbPrompt.provider) {
    logPublic("MICRO-EAV", "FATAL: eav_extraction active version must have provider set in DB");
    return {
      generatedCount: 0,
      costUsd: 0,
      entityNameToDbId: Object.fromEntries(existingNameToId),
      debug: emptyDebug({ error: "provider_not_set", entities_processed: entitiesToProcess.length }),
    };
  }

  // Config z DB (zero hardcode). Model fallback = provider string (Claude convention dla v3).
  const providerName = dbPrompt.provider;
  const modelUsed = dbPrompt.modelId ?? providerName;
  const temperature = dbPrompt.temperature ?? 0.3;

  const entitiesJson = JSON.stringify(
    entitiesToProcess.map(e => ({
      entity_canonical_name: e.entity_name,
      entity_type: e.entity_type || "concept",
      description: e.description || "",
      node_titles: [nodeTitle],
    })),
    null,
    2,
  );

  const templateVars: Record<string, string> = {
    map_name: mapContext.map_name || "",
    macro_statement: mapContext.macro_statement || "",
    domain_context: mapContext.macro_statement || nodeTitle,
    scope_in: (mapContext.scope_in || []).join(", "),
    scope_out: (mapContext.scope_out || []).join(", "),
    language: language || "polski",
    entities_json: entitiesJson,
    // Zachowane dla backward compat z v2 template vars jesli kiedys ktos odpali
    // starsza wersje promptu — v3 tego nie uzywa, ale fillTemplate ignoruje nadmiar.
    nodes_json: JSON.stringify([{ title: nodeTitle, document_role: documentRole, canonical_question: canonicalQuestion }]),
  };

  const prompt = fillTemplate(dbPrompt.template, templateVars);
  const provider = getLLMProvider(providerName);

  const response = await provider.call(prompt, {
    model: modelUsed,
    temperature,
    step: "eav_extraction",
    mapId, nodeId,
  });

  // 6. Parse and INSERT EAV
  let generatedCount = 0;
  let skippedEmptyAttrs = 0;
  let lookupMisses = 0;
  let parseError: string | null = null;
  try {
    const parsed = parseLLMJSON<{ entities: Array<{ entity_canonical_name: string; attributes: Array<{ attribute_name: string; value: string; value_type: string; confidence: number }> }> }>(response.text);

    for (const eavEntity of parsed.entities || []) {
      const lookupKey = eavEntity.entity_canonical_name.toLowerCase();
      const entityId = existingNameToId.get(lookupKey);
      if (!entityId) {
        lookupMisses++;
        logDebug(
          "MICRO-EAV",
          `Lookup miss: "${eavEntity.entity_canonical_name}" (key="${lookupKey}") — available keys: ${[...existingNameToId.keys()].join(" | ")}`,
        );
        continue;
      }

      const attrs = eavEntity.attributes || [];
      if (attrs.length === 0) {
        skippedEmptyAttrs++;
        continue;
      }

      for (const attr of attrs) {
        const { error } = await (supabase as any)
          .from("thematic_entity_attributes")
          .insert({
            entity_id: entityId,
            user_id: userId,
            map_id: mapId,
            attribute_name: attr.attribute_name,
            value: attr.value,
            value_type: normalizeValueType(attr.value_type),
            confidence: attr.confidence || 0.8,
            source: "ai",
          });
        if (error) {
          logDebug("MICRO-EAV", `INSERT error: ${error.message} (${attr.attribute_name})`);
        } else {
          generatedCount++;
        }
      }
    }
  } catch (err) {
    parseError = err instanceof Error ? err.message : "unknown";
    logPublic("MICRO-EAV", `Parse error: ${parseError}`);
    logDebug(
      "MICRO-EAV",
      `RAW response that failed to parse (${response.text.length} chars):\n${response.text.slice(0, 2000)}`,
    );
  }

  const costUsd = response.costUsd || 0;
  logPublic("MICRO-EAV", `Generated ${generatedCount} EAV for ${entitiesToProcess.length} entities — $${costUsd.toFixed(4)}`);

  const debug: MicroEavDebug = {
    entities_processed: entitiesToProcess.length,
    llm_response_chars: response.text?.length ?? 0,
    raw_response_sample: (response.text ?? "").slice(0, 1000),
    generated_count: generatedCount,
    skipped_empty_attrs: skippedEmptyAttrs,
    lookup_misses: lookupMisses,
    parse_error: parseError,
    model_used: modelUsed,
    provider_used: providerName,
  };

  // existingNameToId zawiera juz pre-existing + nowo utworzone thematic_entities.id.
  // Zwracamy wszystko zeby orchestrator mogl zbudowac distribution entity_db_ids mapping.
  return {
    generatedCount,
    costUsd,
    entityNameToDbId: Object.fromEntries(existingNameToId),
    debug,
  };
}
